import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, CircularProgress,
  Alert, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
  Box, Switch, Paper, LinearProgress, Checkbox
} from '@mui/material';
import { booksAPI } from '../../services/api';
import PublishIcon from '@mui/icons-material/Publish';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloudIcon from '@mui/icons-material/Cloud';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MergeIcon from '@mui/icons-material/Merge';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Import the new BookPreviewCard component
import BookPreviewCard from './BookPreviewCard';

// Data source providers with labels and descriptions
const PROVIDERS = {
  'hybrid': {
    value: 'hybrid',
    label: 'Hybrid (Recommended)',
    icon: <MergeIcon fontSize="small" />,
    description: 'Combines data from multiple sources for best results'
  },
  'openlibrary': {
    value: 'openlibrary',
    label: 'OpenLibrary',
    icon: <MenuBookIcon fontSize="small" />,
    description: 'Fetch basic book metadata from OpenLibrary (fast)'
  },
  'google': {
    value: 'google',
    label: 'Google Books',
    icon: <CloudIcon fontSize="small" />,
    description: 'Fetch detailed metadata from Google Books API'
  }
};

const IsbnBookDialog = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  // Existing state variables
  const [isbn, setIsbn] = useState('');
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState('hybrid'); // Change default to hybrid
  const [fetchedBook, setFetchedBook] = useState(null);
  
  // New state variables for bulk processing
  const [bulkMode, setBulkMode] = useState(false);
  const [isbnList, setIsbnList] = useState([]);
  const [currentIsbnIndex, setCurrentIsbnIndex] = useState(0);
  const [processedBooks, setProcessedBooks] = useState([]);
  const [skippedBooks, setSkippedBooks] = useState([]);
  const [publishedBooks, setPublishedBooks] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProcessingComplete, setBulkProcessingComplete] = useState(false);
  const [autoPublish, setAutoPublish] = useState(false);

  // Add new state variables for delete functionality
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  
  // Reset state when dialog opens or closes
  useEffect(() => {
    if (!open) {
      // Reset bulk processing state when dialog closes
      setBulkMode(false);
      setIsbnList([]);
      setCurrentIsbnIndex(0);
      setProcessedBooks([]);
      setSkippedBooks([]);
      setPublishedBooks([]);
      setBulkProcessing(false);
      setBulkProcessingComplete(false);
    }
  }, [open]);

  useEffect(() => {
    // Reset processedBooks when dialog opens or closes
    if (!open) {
      setProcessedBooks([]);
    }
  }, [open, setProcessedBooks]); // Add setProcessedBooks to dependency array
  
  const handleIsbnChange = (e) => {
    if (bulkMode) {
      // For bulk mode, allow comma-separated values
      setIsbn(e.target.value);
    } else {
      // For single mode, clean input as before
      const cleanedValue = e.target.value.replace(/[^0-9X-]/gi, '');
      setIsbn(cleanedValue);
    }
  };
  
  const handleProviderChange = (e) => {
    setProvider(e.target.value);
  };
  
  const handleBulkModeToggle = () => {
    // Reset fields when toggling mode
    setIsbn('');
    setError(null);
    setFetchedBook(null);
    setBulkMode(!bulkMode);
  };
  
  const parseIsbnList = () => {
    // Split by comma, trim whitespace, and filter out empty entries
    return isbn.split(',')
      .map(item => item.trim().replace(/[^0-9X]/gi, ''))
      .filter(item => item.length > 0);
  };
  
  const startBulkProcessing = () => {
    const isbns = parseIsbnList();
    
    if (isbns.length === 0) {
      setError('Please enter at least one valid ISBN');
      return;
    }
    
    setIsbnList(isbns);
    setCurrentIsbnIndex(0);
    setProcessedBooks([]);
    setSkippedBooks([]);
    setPublishedBooks([]);
    setBulkProcessing(true);
    setBulkProcessingComplete(false);
    
    // Start processing the first ISBN
    processCurrentIsbn(isbns, 0);
  };
  
  const processCurrentIsbn = async (isbns, index) => {
    if (index >= isbns.length) {
      // All ISBNs have been processed
      setBulkProcessingComplete(true);
      setBulkProcessing(false);
      return;
    }
    
    const currentIsbn = isbns[index];
    setCurrentIsbnIndex(index);
    
    try {
      setLoading(true);
      setError(null);
      
      let response;
      
      // Call the API with the appropriate endpoint based on the provider
      if (provider === 'openlibrary') {
        response = await booksAPI.createBookFromOpenLibrary({
          isbn: currentIsbn
        });
      } else if (provider === 'google') {
        response = await booksAPI.createBookFromGoogleBooks({
          isbn: currentIsbn
        });
      } else if (provider === 'hybrid') {
        // Use the hybrid endpoint that combines data from multiple sources
        response = await booksAPI.createBookFromHybridSources({
          isbn: currentIsbn
        });
      }
      
      // Extract the book object from the response
      const bookData = response?.data?.book;
      
      if (bookData) {
        console.log('Book data received:', bookData);
        setFetchedBook(bookData);
        
        // Add to processed books
        setProcessedBooks(prev => [...prev, bookData]);
        
        // If autoPublish is enabled, automatically publish the book and move to the next one
        if (autoPublish) {
          try {
            setPublishing(true);
            
            // Update book status to published
            await booksAPI.patchBook(bookData.id, { status: 'published' });
            
            // Add to published books
            setPublishedBooks(prev => [...prev, bookData]);
            
            // Notify parent of success
            if (onSuccess) {
              onSuccess(bookData, true);
            }
            
            setPublishing(false);
            
            // Move to next ISBN without waiting for user
            moveToNextIsbn(isbns, index);
          } catch (error) {
            console.error('Error auto-publishing book:', error);
            setError('Failed to auto-publish book. Continuing with manual review.');
            setPublishing(false);
          }
        }
      } else {
        throw new Error('Invalid response format from API');
      }
      
    } catch (error) {
      console.error(`Error creating book from ISBN ${currentIsbn}:`, error);
      setError(
        error.response?.data?.message || 
        `Failed to create book from ISBN ${currentIsbn}. Please check the ISBN and try again.`
      );
      setFetchedBook(null);
      
      // Add to skipped books when there's an error
      setSkippedBooks(prev => [...prev, { isbn: currentIsbn, error: error.message }]);
      
      // Move to next ISBN after a brief delay
      setTimeout(() => {
        moveToNextIsbn(isbns, index);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePublishBulkBook = async () => {
    if (!fetchedBook?.id) return;
    
    try {
      setPublishing(true);
      
      // Update book status to published
      await booksAPI.patchBook(fetchedBook.id, { status: 'published' });
      
      // Add to published books
      setPublishedBooks(prev => [...prev, fetchedBook]);
      
      // Notify parent of success
      if (onSuccess) {
        onSuccess(fetchedBook, true);
      }
      
      setPublishing(false);
      
      // Move to next ISBN
      moveToNextIsbn(isbnList, currentIsbnIndex);
      
    } catch (error) {
      console.error('Error publishing book:', error);
      setError('Failed to publish book. Please try again.');
      setPublishing(false);
    }
  };
  
  const handleSkipBook = () => {
    // Add to skipped books
    setSkippedBooks(prev => [...prev, { isbn: isbnList[currentIsbnIndex], book: fetchedBook }]);
    
    // Move to next ISBN
    moveToNextIsbn(isbnList, currentIsbnIndex);
  };
  
  const moveToNextIsbn = (isbns, currentIndex) => {
    // Clear current book and error
    setFetchedBook(null);
    setError(null);
    
    // Move to next ISBN
    const nextIndex = currentIndex + 1;
    setCurrentIsbnIndex(nextIndex);
    
    if (nextIndex < isbns.length) {
      // Process next ISBN
      processCurrentIsbn(isbns, nextIndex);
    } else {
      // All ISBNs processed
      setBulkProcessingComplete(true);
      setBulkProcessing(false);
    }
  };
  
  // Original single ISBN processing
  const handleSubmit = async () => {
    if (!isbn) {
      setError('Please enter an ISBN');
      return;
    }
    
    // Remove hyphens for API call
    const cleanIsbn = isbn.replace(/-/g, '');
    
    try {
      setLoading(true);
      setError(null);
      
      let response;
      
      // Call the API with the appropriate endpoint based on the provider
      if (provider === 'openlibrary') {
        // Use OpenLibrary-specific endpoint
        response = await booksAPI.createBookFromOpenLibrary({
          isbn: cleanIsbn
        });
      } else if (provider === 'google') {
        // Use Google Books-specific endpoint
        response = await booksAPI.createBookFromGoogleBooks({
          isbn: cleanIsbn
        });
      } else if (provider === 'hybrid') {
        response = await booksAPI.createBookFromHybridSources({
          isbn: cleanIsbn
        });
      }
      
      // Extract the book object from the response
      const bookData = response?.data?.book;
      
      if (bookData) {
        console.log('Book data received:', bookData);
        setFetchedBook(bookData);
        
        // Notify parent component of success (but don't close dialog)
        if (onSuccess) {
          onSuccess(bookData, false); // false indicates not published yet
        }
      } else {
        throw new Error('Invalid response format from API');
      }
      
    } catch (error) {
      console.error('Error creating book from ISBN:', error);
      setError(
        error.response?.data?.message || 
        'Failed to create book from ISBN. Please check the ISBN and try again.'
      );
      setFetchedBook(null);
    } finally {
      setLoading(false);
    }
  };

  // Existing functions
  const handleReset = () => {
    setIsbn('');
    setFetchedBook(null);
    setError(null);
  };

  const handlePublish = async () => {
    if (!fetchedBook?.id) return;
    
    try {
      setPublishing(true);
      
      // Update book status to published
      await booksAPI.patchBook(fetchedBook.id, { status: 'published' });
      
      // Notify parent component of success (with published=true)
      if (onSuccess) {
        onSuccess({...fetchedBook, status: 'published'}, true);
      }
      
      // Close dialog and reset state
      handleClose(true);
      
    } catch (error) {
      console.error('Error publishing book:', error);
      setError('Failed to publish book. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleOpenDeleteConfirm = () => {
    setConfirmDeleteOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setConfirmDeleteOpen(false);
  };

  const handleDeleteBook = async () => {
    if (!fetchedBook?.id) return;

    try {
      setDeleteLoading(true);

      // Call the API to delete the book
      await booksAPI.deleteBook(fetchedBook.id);

      // Close the confirmation dialog
      setConfirmDeleteOpen(false);

      // Notify parent of success (optional)
      if (onSuccess) {
        onSuccess(fetchedBook, false, 'deleted');
      }

      // Close the main dialog and reset state
      handleClose(true);

    } catch (error) {
      console.error('Error deleting book:', error);
      setError('Failed to delete book. Please try again.');
      setConfirmDeleteOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // New function to navigate to book details page
  const handleGoToBook = () => {
    if (fetchedBook?.id) {
      navigate(`/books/${fetchedBook.id}`);
      handleClose(false);
    }
  };

  const handleClose = (resetState = true) => {
    if (resetState) {
      setIsbn('');
      setFetchedBook(null);
      setError(null);
      setBulkMode(false);
      setIsbnList([]);
      setCurrentIsbnIndex(0);
      setProcessedBooks([]);
      setSkippedBooks([]);
      setPublishedBooks([]);
      setBulkProcessing(false);
      setBulkProcessingComplete(false);
    }
    onClose();
  };
  
  // Calculate progress percentage for bulk processing
  const calculateProgress = () => {
    if (isbnList.length === 0) return 0;
    return ((currentIsbnIndex) / isbnList.length) * 100;
  };
  
  // Render the bulk processing results summary
  const renderBulkSummary = () => {
    return (
      <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>Bulk Processing Complete</Typography>
        
        <Typography variant="body1">
          Processed {isbnList.length} ISBNs:
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <Box component="li">{publishedBooks.length} books published</Box>
            <Box component="li">{skippedBooks.length} books skipped</Box>
          </Box>
        </Typography>
        
        {publishedBooks.length > 0 && (
          <>
            <Typography variant="subtitle2" mt={2}>Published Books:</Typography>
            <Box sx={{ maxHeight: '200px', overflowY: 'auto', mt: 1 }}>
              {publishedBooks.map((book, index) => (
                <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {book.title} by {book.author}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
        
        {skippedBooks.length > 0 && (
          <>
            <Typography variant="subtitle2" color="error" mt={2}>Skipped Books:</Typography>
            <Box sx={{ maxHeight: '200px', overflowY: 'auto', mt: 1 }}>
              {skippedBooks.map((item, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    ISBN: {item.isbn} - {item.book ? `"${item.book.title}" (skipped by user)` : item.error}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Paper>
    );
  };
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={() => !loading && !publishing && !deleteLoading && handleClose()} 
        maxWidth={fetchedBook ? "md" : "sm"} 
        fullWidth
      >
        <DialogTitle>
          {bulkMode ? "Add Multiple Books by ISBN" : "Add Book by ISBN"}
        </DialogTitle>
        
        <DialogContent>
          {!fetchedBook && !bulkProcessingComplete ? (
            // Initial ISBN input form
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Enter an ISBN to automatically create a book with details fetched from online databases.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={bulkMode}
                      onChange={handleBulkModeToggle}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormatListNumberedIcon fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="body2">Bulk Mode</Typography>
                    </Box>
                  }
                />
              </Box>
              
              <TextField
                autoFocus
                margin="dense"
                id="isbn"
                label={bulkMode ? "ISBNs (comma-separated)" : "ISBN"}
                type="text"
                fullWidth
                variant="outlined"
                value={isbn}
                onChange={handleIsbnChange}
                disabled={loading || bulkProcessing}
                placeholder={bulkMode ? "Enter multiple ISBNs separated by commas" : "Enter 10 or 13 digit ISBN (hyphens optional)"}
                helperText={bulkMode ? "Example: 9781451648546, 0307265439, 9780596517748" : "Example: 9781451648546 or 0-307-26543-9"}
                sx={{ my: 2 }}
                multiline={bulkMode}
                rows={bulkMode ? 3 : 1}
              />
              
              <FormControl component="fieldset" sx={{ my: 2 }}>
                <FormLabel component="legend">Data Source</FormLabel>
                <RadioGroup
                  name="provider"
                  value={provider}
                  onChange={handleProviderChange}
                >
                  {Object.values(PROVIDERS).map((providerOption) => (
                    <FormControlLabel 
                      key={providerOption.value}
                      value={providerOption.value} 
                      control={<Radio size="small" />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ mr: 1 }}>{providerOption.icon}</Box>
                          <Box>
                            <Typography variant="body2">{providerOption.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {providerOption.description}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      disabled={loading || bulkProcessing} 
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              
              {bulkMode && (
                <>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Bulk Mode Instructions:</strong>
                    </Typography>
                    <Typography variant="body2" component="ol" sx={{ pl: 2, mt: 1 }}>
                      <li>Enter multiple ISBNs separated by commas</li>
                      <li>Each ISBN will be processed one at a time</li>
                      <li>You'll need to confirm or skip each book individually</li>
                    </Typography>
                  </Alert>
                  
                  {/* Add AutoPublish checkbox */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autoPublish}
                        onChange={(e) => setAutoPublish(e.target.checked)}
                        color="primary"
                        disabled={loading || bulkProcessing}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Automatically publish books and continue to next ISBN
                      </Typography>
                    }
                    sx={{ mt: 2, display: 'block' }}
                  />
                </>
              )}
              
              {bulkProcessing && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      Processing ISBN {currentIsbnIndex + 1} of {isbnList.length}
                    </Typography>
                    <Typography variant="body2">
                      {Math.round(calculateProgress())}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={calculateProgress()} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}
            </>
          ) : bulkProcessingComplete ? (
            // Show summary when bulk processing is complete
            renderBulkSummary()
          ) : (
            // Use the new BookPreviewCard component
            <BookPreviewCard 
              book={fetchedBook}
              bulkMode={bulkMode}
              currentIsbn={bulkMode ? isbnList[currentIsbnIndex] : ''}
              currentIndex={currentIsbnIndex}
              totalCount={isbnList.length}
              progress={calculateProgress()}
            />
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!fetchedBook && !bulkProcessingComplete ? (
            // Initial actions
            <>
              <Button onClick={() => handleClose()} disabled={loading || bulkProcessing}>
                Cancel
              </Button>
              <Button 
                onClick={bulkMode ? startBulkProcessing : handleSubmit} 
                color="primary" 
                variant="contained" 
                disabled={loading || bulkProcessing || !isbn}
                startIcon={loading || bulkProcessing ? <CircularProgress size={16} /> : null}
              >
                {loading || bulkProcessing ? 'Processing...' : bulkMode ? 'Start Bulk Processing' : 'Create Book'}
              </Button>
            </>
          ) : bulkProcessingComplete ? (
            // Actions when bulk processing is complete
            <Button 
              onClick={() => handleClose()} 
              color="primary" 
              variant="contained"
            >
              Close
            </Button>
          ) : (
            // Actions after book is fetched
            bulkMode ? (
              <>
                <Button
                  onClick={handleOpenDeleteConfirm}
                  color="error"
                  disabled={publishing || deleteLoading}
                  startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
                  sx={{ mr: 'auto' }}
                >
                  Delete
                </Button>
                <Button
                  onClick={handleGoToBook}
                  color="info"
                  disabled={publishing || deleteLoading || !fetchedBook?.id}
                  startIcon={<OpenInNewIcon />}
                >
                  Go to Book
                </Button>
                <Button
                  onClick={handleSkipBook}
                  color="inherit"
                  disabled={publishing || deleteLoading}
                  startIcon={<SkipNextIcon />}
                >
                  Skip Book
                </Button>
                <Button 
                  onClick={handlePublishBulkBook}
                  color="success"
                  variant="contained" 
                  disabled={publishing || deleteLoading}
                  startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />}
                >
                  {publishing ? 'Publishing...' : 'Publish and Continue'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleOpenDeleteConfirm}
                  color="error"
                  disabled={publishing || deleteLoading}
                  startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
                  sx={{ mr: 'auto' }}
                >
                  Delete
                </Button>
                <Button 
                  onClick={handleReset} 
                  disabled={publishing || deleteLoading}
                  color="inherit"
                >
                  Add Another ISBN
                </Button>
                <Button
                  onClick={handleGoToBook}
                  color="info"
                  disabled={publishing || deleteLoading || !fetchedBook?.id}
                  startIcon={<OpenInNewIcon />}
                >
                  Go to Book
                </Button>
                <Button 
                  onClick={() => handleClose()} 
                  disabled={publishing || deleteLoading}
                >
                  Close
                </Button>
                <Button 
                  onClick={handlePublish}
                  color="success"
                  variant="contained" 
                  disabled={publishing || deleteLoading}
                  startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />}
                >
                  {publishing ? 'Publishing...' : 'Publish Now'}
                </Button>
              </>
            )
          )}
        </DialogActions>
      </Dialog>
      
      {/* Add a confirmation dialog for delete */}
      <Dialog open={confirmDeleteOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{fetchedBook?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteBook} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IsbnBookDialog;
